import get from 'lodash.get';
import cloneDeep from 'lodash.clonedeep';

import { emptyArray, emptyObject } from 'ui-components/default-props/empty';
import * as CreateLabelAction from '../../store/create-label-content/create-label-content.actions';
import { CSWTypes } from '../../enums/csw-types';
import { LogService } from '../../services/log-service';
import { baseReducer as reducerMapper } from '../../store/core/base-reducer';

import { cleanOptionsBasedOnSelectedServiceId } from '../../services/other-shipping-options/constants';
import * as Actions from './csw.actions';
import { getConfirmation } from './csw.helpers';
import { buildCustoms } from './csw.fulfillment-plan-to-csw';
import { Recipient } from '../../fulfillment-planner/duties-and-taxes/constants';

const initialState = {};

const rateSuccess = (state, { rateMeta }) => {
  const { updatedProperties, rate } = rateMeta;
  return {
    ...state,
    shippingConfiguration: {
      ...state.shippingConfiguration,
      ...updatedProperties,
      rate,
    },
    errors: emptyArray,
  };
};

const rateFailure = (state, { errors }) => ({
  ...state,
  errors,
  shippingConfiguration: {
    ...state.shippingConfiguration,
    rate: {},
  },
});

const actionMap = {
  [Actions.CSW_SETUP_SHIPMENT_CONFIGURATION]: (state, { updatePayload, otherConfiguration }) => {
    const resultingConfiguration = {
      ...state.shippingConfiguration,
      ...updatePayload,
    };
    if (resultingConfiguration.serviceCollectionGuid) {
      resultingConfiguration.serviceId = undefined;
      resultingConfiguration.sellerLabelProviderId = undefined;
    }

    return {
      ...state,
      ...otherConfiguration,
      shippingConfiguration: resultingConfiguration,
    };
  },
  [Actions.CSW_UPDATE_SHIPMENT_CONFIGURATION]: (state, { updatePayload }) => {
    const payloadOptions = get(updatePayload, 'options', emptyObject);
    const currentOptions = get(state, 'shippingConfiguration.options', {});

    Object.keys(currentOptions).forEach((option) => {
      if (payloadOptions[option] === null || payloadOptions[option] === false) {
        delete currentOptions[option];
        delete payloadOptions[option];
      }
    });

    const defaultConfirmation = 'None';
    const confirmation = getConfirmation(payloadOptions) || get(state, 'shippingConfiguration.options.confirmation', defaultConfirmation);

    // set the initial additionalOptions value for rate browser / customer create label
    const availableShippingOptions = updatePayload?.availableShippingOptions;
    if (availableShippingOptions && !currentOptions.additionalOptions) {
      const recipientOption = availableShippingOptions.find((opt) => opt?.additionalOptionValue?.prepaidDuty === Recipient);
      if (recipientOption) {
        currentOptions.additionalOptions = [recipientOption];
      }
    }

    return {
      ...state,
      shippingConfiguration: {
        ...state.shippingConfiguration,
        ...updatePayload,
        options: {
          ...currentOptions,
          ...payloadOptions,
          confirmation,
        },
      },
    };
  },
  [Actions.CSW_SYNC_CONTEXT_TO_REDUCER]: (state, { payload }) => {
    return {
      ...state,
      shippingConfiguration: {
        ...state.shippingConfiguration,
        ...payload,
      },
    };
  },
  [Actions.CSW_UPDATE_SHIPMENT_CONFIGURATION_OPTIONS]: (state, { optionName, optionValue }) => ({
    ...state,
    shippingConfiguration: {
      ...state.shippingConfiguration,
      options: {
        ...get(state, 'shippingConfiguration.options', emptyObject),
        [optionName]: optionValue,
      },
    },
  }),
  [Actions.CSW_UPDATE_AVAILABLE_SHIPPING_SERVICES]: (state, { updatedAvailableShippingServices }) => ({
    ...state,
    shippingConfiguration: {
      ...state.shippingConfiguration,
      availableSellerShippingServices: updatedAvailableShippingServices,
    },
  }),
  [Actions.CSW_UPDATE_NEEDS_CUSTOMS_REQUIREMENTS]: (state, { updatedNeedsCustomsRequirements }) => {
    return {
      ...state,
      shippingConfiguration: {
        ...state.shippingConfiguration,
        customs: buildCustoms(updatedNeedsCustomsRequirements.needsCustoms, state.shippingConfiguration.customs),
        properties: {
          ...get(state, 'shippingConfiguration.properties', {}),
          ...updatedNeedsCustomsRequirements.needsCustoms,
        },
      },
    };
  },
  [Actions.CSW_UPDATE_SERVICE_AND_OTHER_SHIPPING_OPTIONS]: (state, { serviceId }) => ({
    ...state,
    shippingConfiguration: {
      ...state.shippingConfiguration,
      options: cleanOptionsBasedOnSelectedServiceId(serviceId, state.shippingConfiguration && state.shippingConfiguration.options),
    },
  }),
  [Actions.CSW_UPDATE_SHIPMENT_CONFIGURATION_PACKAGES]: (state, { updatePayload, index, preserveNonZeroIndex }) => {
    const statePackages = get(state, 'shippingConfiguration.packages', emptyArray);
    const statePackageToUpdate = get(statePackages, index);
    const updatedPackage = {
      ...statePackageToUpdate,
      ...updatePayload,
      dimensions: {
        ...get(statePackageToUpdate, 'dimensions'),
        ...get(updatePayload, 'dimensions'),
      },
    };
    const packageId = get(updatePayload, 'packageId');
    let packages;

    if (packageId) {
      updatedPackage.packageTypeId = get(packageId.split('-'), '0');
    }

    if (index === 0 && !preserveNonZeroIndex) {
      packages = [updatedPackage];
    } else {
      packages = [...statePackages.slice(0, index), updatedPackage, ...statePackages.slice(index + 1)];
    }

    return {
      ...state,
      shippingConfiguration: {
        ...state.shippingConfiguration,
        packages,
      },
    };
  },
  [Actions.CSW_CHANGE_INSURANCE_VALUE_ON_ALL_PACKAGES]: (state, { updatePayload }) => {
    const statePackages = get(state, 'shippingConfiguration.packages', []);
    if (!statePackages.length) {
      return state;
    }

    const packages = statePackages.map((pack) => {
      return {
        ...pack,
        ...updatePayload,
      };
    });
    return {
      ...state,
      shippingConfiguration: {
        ...state.shippingConfiguration,
        packages,
      },
    };
  },
  [Actions.CSW_UPDATE_SHIPMENT_CONFIGURATION_PACKAGES_FROM_PRESET]: (state, { updatePayload }) => {
    const statePackages = get(state, 'shippingConfiguration.packages', []);
    const packageId = get(updatePayload, 'packageId');
    if (packageId) {
      // oxlint-disable-next-line no-param-reassign
      updatePayload.packageTypeId = get(packageId.split('-'), '0');
    }
    if (statePackages.length > 1) {
      return {
        ...state,
        shippingConfiguration: {
          ...state.shippingConfiguration,
          packages: [updatePayload],
        },
      };
    }
    const cleanedUpdatePayload = { ...updatePayload };
    Object.keys(cleanedUpdatePayload).forEach((property) => {
      const value = cleanedUpdatePayload[property];
      if (value !== null && value !== undefined) {
        return;
      }
      delete cleanedUpdatePayload[property];
    });

    const firstPackage = get(statePackages, '0', emptyObject);
    const mergedPackage = {
      ...firstPackage,
      ...cleanedUpdatePayload,
    };

    return {
      ...state,
      shippingConfiguration: {
        ...state.shippingConfiguration,
        packages: [mergedPackage],
      },
    };
  },
  [Actions.CSW_UPDATE_FULFILLMENT_PLAN_AND_RATE_FAILURE]: rateFailure,
  [Actions.CSW_EXTERNAL_FULFILLMENT_SAVE_AND_RATE_FAILURE]: rateFailure,
  [Actions.CSW_UPDATE_FULFILLMENT_PLAN_AND_RATE_SUCCESS]: rateSuccess,
  [Actions.CSW_EXTERNAL_FULFILLMENT_SAVE_AND_RATE_SUCCESS]: rateSuccess,
  [Actions.CSW_SAVE_MULTIPACKAGE_SET]: (state, { packages }) => {
    return {
      ...state,
      shippingConfiguration: {
        ...state.shippingConfiguration,
        packages: cloneDeep(packages),
      },
    };
  },
  [Actions.CSW_SET_PENDING_DOCUMENTS]: (state, { pendingDocument }) => ({
    ...state,
    shippingConfiguration: {
      ...state.shippingConfiguration,
      customs: {
        ...get(state, 'shippingConfiguration.customs', emptyObject),
        pendingDocuments: pendingDocument,
      },
    },
  }),
  [Actions.CSW_RESET]: () => initialState,
  [Actions.CSW_ENTER_RETURNS_MODE]: (state) => ({
    ...state,
    isReturn: true,
  }),
  [Actions.CSW_EXIT_RETURNS_MODE]: () => ({
    ...initialState,
    isReturn: false,
  }),
  [Actions.CSW_ENTER_EXTERNAL_FULFILLMENT_MODE]: (state) => ({
    ...state,
    isExternalFulfillment: true,
    type: CSWTypes.externalFulfillments.id,
    shippingConfiguration: {
      ...state.shippingConfiguration,
      rate: {},
    },
    errors: [],
  }),
  [Actions.CSW_EXIT_EXTERNAL_FULFILLMENT_MODE]: (state) => ({
    ...state,
    isExternalFulfillment: false,
    type: undefined,
    shippingConfiguration: {
      ...state.shippingConfiguration,
      rate: {},
    },
    errors: [],
  }),
  [CreateLabelAction.CREATE_LABEL_CONTENT_UPDATE_RETURNS_FLAG]: (state, { isCreatingReturnLabel }) => ({
    ...state,
    isReturn: isCreatingReturnLabel,
  }),
  [Actions.CSW_UPDATE_TYPE]: (state, { cswTypeId }) => ({
    ...state,
    type: cswTypeId,
  }),
};

const mappedReducer = reducerMapper(initialState, actionMap);

const performActionOnFulfillmentPlan = (state, action) => {
  const fulfillmentPlanId = get(action, 'fulfillmentPlanId');

  if (!fulfillmentPlanId || !actionMap[action.type]) {
    if (actionMap[action.type] && !fulfillmentPlanId) {
      LogService.logError(new Error('Interacting with CSW without a fulfillmentPlanId in the action'), action);
    }

    return state;
  }

  return {
    ...state,
    [fulfillmentPlanId]: mappedReducer(state[fulfillmentPlanId], action),
  };
};

const typesThatDoNotModifyState = [Actions.CSW_ENTER_RETURNS_MODE];

export const reducer = (state = {}, action) => {
  if (typesThatDoNotModifyState.includes(action.type)) {
    return state;
  }

  return performActionOnFulfillmentPlan(state, action);
};

export { namespace } from './csw.reducer.metadata';
